import React from 'react';
import { Box, Typography, Tooltip } from '@mui/material';

// Ce composant attend en props :
// produit, achatsByProduct, repricingRuleData, thresholds, getProfit, getMargin, getROI, getTVADetails

function getColorForValue(value, thresholds) {
  if (value === null || value === undefined) return { bg: 'inherit', text: 'inherit' };
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return { bg: 'inherit', text: 'inherit' };
  if (numValue >= thresholds.high) return { bg: '#e8f5e9', text: '#2e7d32' };
  if (numValue >= thresholds.medium) return { bg: '#fff3e0', text: '#ef6c00' };
  return { bg: '#ffebee', text: '#c62828' };
}

const StatistiquesProduit = ({
  produit,
  achatsByProduct,
  repricingRuleData,
  thresholds,
  getProfit,
  getMargin,
  getROI,
  getTVADetails
}) => {
  // 1. Cherche la règle FR
  const rulesForProduct = repricingRuleData[produit.id] || {};
  let rule = rulesForProduct['FR'];
  if (!rule) {
    const allRules = Object.values(rulesForProduct);
    if (allRules.length > 0) rule = allRules[0];
  }
  let price, fraisAmazon, prixAchat, fraisPreparation, fraisLivraison, tvaDeductible, txTVA, pays, fba, referral, digital, currency;
  let achat = null;
  if (rule && rule.achat_id && achatsByProduct[produit.id]) {
    achat = achatsByProduct[produit.id].find(a => a.id === rule.achat_id);
  }
  if (rule) {
    price = Number(rule.target_price);
    fba = Number(rule.FBAFees || 0);
    referral = Number(rule.ReferralFees || 0);
    digital = Number(rule.DigitalFees || 0);
    fraisAmazon = fba + referral + digital;
    prixAchat = achat?.prixAchat ?? achat?.["Prix d'achat"] ?? 0;
    fraisPreparation = achat?.frais_preparation ?? 0;
    fraisLivraison = achat?.frais_livraison ?? 0;
    tvaDeductible = achat?.TVADeductible ?? false;
    txTVA = achat?.TxTVA ?? 20;
    pays = rule.marketplace || 'FR';
    currency = rule.currency || 'EUR';
  } else {
    // 3. Fallback sur le prix de vente moyen
    price = Number(produit["Prix de vente moyen"] || 0);
    fba = referral = digital = fraisAmazon = 0;
    prixAchat = 0;
    fraisPreparation = 0;
    fraisLivraison = 0;
    tvaDeductible = false;
    txTVA = 20;
    pays = 'FR';
    currency = 'EUR';
  }
  const profit = getProfit({ prixVente: price, prixAchat, fraisAmazon, fraisPreparation, fraisLivraison, tvaDeductible, txTVA, pays });
  const margin = getMargin({ profit, prixVente: price });
  const coutTotal = prixAchat + fraisPreparation + fraisLivraison;
  const roi = getROI({ profit, coutTotal });
  const tvaDetails = getTVADetails({ prixVente: price, prixAchat, tvaDeductible, txTVA, pays });

  return (
    <Box sx={{
      flex: 1,
      minWidth: 220,
      bgcolor: '#f9f9fb',
      p: 2.5,
      display: 'flex',
      flexDirection: 'column',
      borderRight: { md: '1px solid #e3e6ef' },
      gap: 1,
      borderRadius: 2,
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Statistiques</Typography>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 1.2,
        alignItems: 'center',
        mb: 1,
      }}>
        <Typography color="text.secondary" fontSize={14}>Marge</Typography>
        {(() => {
          const colors = getColorForValue(margin, thresholds.marge);
          return (
            <Box sx={{ bgcolor: colors.bg, color: colors.text, borderRadius: 1, px: 1, py: 0.2, fontWeight: 600, fontSize: 15, textAlign: 'right' }}>
              {Number.isFinite(margin) ? margin.toFixed(2) : 0} %
            </Box>
          );
        })()}
        <Typography color="text.secondary" fontSize={14}>ROI</Typography>
        {(() => {
          const colors = getColorForValue(roi, thresholds.roi);
          return (
            <Box sx={{ bgcolor: colors.bg, color: colors.text, borderRadius: 1, px: 1, py: 0.2, fontWeight: 600, fontSize: 15, textAlign: 'right' }}>
              {Number.isFinite(roi) ? roi.toFixed(2) : 0} %
            </Box>
          );
        })()}
        <Typography color="text.secondary" fontSize={14}>Profit</Typography>
        {(() => {
          const colors = getColorForValue(profit, thresholds.profit);
          return (
            <Box sx={{ bgcolor: colors.bg, color: colors.text, borderRadius: 1, px: 1, py: 0.2, fontWeight: 600, fontSize: 15, textAlign: 'right' }}>
              {Number.isFinite(profit) ? profit.toFixed(2) : 0} €
            </Box>
          );
        })()}
        <Typography color="text.secondary" fontSize={14}>TVA</Typography>
        <Tooltip title={<div>
          <div><b>TVA collectée :</b> {tvaDetails?.tvaCollectee?.toFixed(2)} EUR (taux {tvaDetails?.txTVACollecte}%)</div>
          <div><b>TVA déductible :</b> {tvaDetails?.tvaDeductible?.toFixed(2)} EUR {tvaDeductible ? `(taux ${tvaDetails?.txTVADeductible}%)` : '(non déductible)'}</div>
        </div>} arrow>
          <Box sx={{ bgcolor: '#ffebee', color: '#c62828', borderRadius: 1, px: 1, py: 0.2, fontWeight: 600, fontSize: 15, textAlign: 'right', cursor: 'help' }}>
            {tvaDetails?.tvaNette?.toFixed(2) ?? 0} €
          </Box>
        </Tooltip>
        <Typography color="text.secondary" fontSize={14}>Frais Amazon</Typography>
        <Tooltip title={<div style={{ fontSize: 13 }}>
          <div><b>FBA :</b> {fba?.toFixed(2)} €</div>
          <div><b>Referral :</b> {referral?.toFixed(2)} €</div>
          <div><b>Digital :</b> {digital?.toFixed(2)} €</div>
          <div style={{ marginTop: 6 }}><b>Total :</b> {fraisAmazon?.toFixed(2)} €</div>
        </div>} arrow>
          <Box sx={{ bgcolor: '#ffebee', color: '#c62828', borderRadius: 1, px: 1, py: 0.2, fontWeight: 600, fontSize: 15, textAlign: 'right', cursor: 'help' }}>
            {fraisAmazon?.toFixed(2)} €
          </Box>
        </Tooltip>
        <Typography color="text.secondary" fontSize={14}>Stock</Typography>
        <Typography fontWeight={500} color="text.primary" fontSize={14}>{produit.Stock ?? '-'}</Typography>
        <Typography color="text.secondary" fontSize={14}>Prix moyen</Typography>
        <Typography fontWeight={500} color="text.primary" fontSize={14}>{produit["Prix de vente moyen"] ? Number(produit["Prix de vente moyen"]).toFixed(2) + ' €' : '-'}</Typography>
      </Box>
    </Box>
  );
};

export default StatistiquesProduit; 